import React, { useState, useEffect, useMemo } from 'react';
import {
  getPackages,
  getAddOns,
  getCategories,
  subscribeToPackages,
  subscribeToAddOns,
  subscribeToCategories,
  createPackage,
  updatePackage,
  deletePackage,
  createAddOn,
  updateAddOn,
  deleteAddOn,
  migrateFromMock,
  uploadGalleryImage,
} from '../../services/firestore';
import { services as mockServices } from '../../mock';
import {
  Package,
  Plus,
  Edit2,
  Trash2,
  Loader2,
  X,
  Search,
  Layers,
  Database,
  CheckCircle,
  Upload,
  Link as LinkIcon,
  Image as ImageIcon,
} from 'lucide-react';
import { subscribeToGallery } from '../../services/firebase';
import { toastSuccess, toastError, toastPromise } from '../../components/admin/ToastProvider';
import { useConfirm, ConfirmDialog } from '../../components/admin/ConfirmDialog';
import { StatsCardSkeleton } from '../../components/admin/Skeleton';
import { PackagesEmptyState, SearchEmptyState, ErrorState } from '../../components/admin/EmptyState';

const AdminPackages = () => {
  // Data
  const [packages, setPackages] = useState([]);
  const [addOns, setAddOns] = useState([]);
  const [categories, setCategories] = useState([]);
  const [galleryItems, setGalleryItems] = useState([]);
  const [loading, setLoading] = useState(true);

  // Search & Filter
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');

  // Active Tab
  const [activeTab, setActiveTab] = useState('packages'); // 'packages' or 'addons'

  // Modal states
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('create'); // 'create' or 'edit'
  const [selectedItem, setSelectedItem] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    categoryId: '',
    subcategoryName: '',
    price: '',
    duration: '',
    includes: '',
    image: '',
    images: [],
    galleryImageIds: [],
    availableAddOns: [],
    isActive: true,
  });
  const [imageSourceType, setImageSourceType] = useState('file');
  const [packageImageFiles, setPackageImageFiles] = useState([]);
  const [packageImagePreviews, setPackageImagePreviews] = useState([]);
  const [imageUrlsInput, setImageUrlsInput] = useState('');
  const [fileError, setFileError] = useState('');
  const [uploadingImage, setUploadingImage] = useState(false);

  useEffect(() => {
    if (packageImageFiles.length === 0) {
      setPackageImagePreviews([]);
      return;
    }

    const nextPreviews = packageImageFiles.map((file) => ({
      name: file.name,
      url: URL.createObjectURL(file),
    }));

    setPackageImagePreviews(nextPreviews);

    return () => {
      nextPreviews.forEach((preview) => URL.revokeObjectURL(preview.url));
    };
  }, [packageImageFiles]);

  // Error/Success states
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Migration states
  const [isMigrating, setIsMigrating] = useState(false);
  const [migrationResult, setMigrationResult] = useState(null);

  // Confirmation dialog hook
  const { confirm, ConfirmDialog } = useConfirm();

  useEffect(() => {
    const unsubscribeCategories = subscribeToCategories((data) => {
      setCategories(data);
    });

    const unsubscribePackages = subscribeToPackages((data) => {
      setPackages(data);
      setLoading(false);
      setError(null);
    });

    const unsubscribeGallery = subscribeToGallery((data) => {
      setGalleryItems(data || []);
    });

    const unsubscribeAddOns = subscribeToAddOns((data) => {
      setAddOns(data);
    });

    return () => {
      unsubscribeCategories();
      unsubscribePackages();
      unsubscribeAddOns();
      unsubscribeGallery();
    };
  }, []);

  // Memoized filtered packages
  const filteredPackages = useMemo(() => {
    return packages.filter((pkg) => {
      const matchesSearch =
        pkg.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (pkg.description && pkg.description.toLowerCase().includes(searchQuery.toLowerCase()));

      const matchesCategory = categoryFilter === 'all' || pkg.categoryId === categoryFilter;

      return matchesSearch && matchesCategory;
    });
  }, [packages, searchQuery, categoryFilter]);

  // Memoized filtered add-ons
  const filteredAddOns = useMemo(() => {
    return addOns.filter((addon) => {
      return (
        addon.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (addon.description && addon.description.toLowerCase().includes(searchQuery.toLowerCase()))
      );
    });
  }, [addOns, searchQuery]);

  // Memoized category name lookup
  const getCategoryName = useMemo(() => {
    const categoryMap = new Map(categories.map(c => [c.id, c.name]));
    return (categoryId) => categoryMap.get(categoryId) || categoryId;
  }, [categories]);

  const galleryImageMap = useMemo(() => {
    return new Map(galleryItems.map((item) => [item.id, item]));
  }, [galleryItems]);

  const galleryImageByUrl = useMemo(() => {
    return new Map(galleryItems.map((item) => [item.image, item.id]));
  }, [galleryItems]);

  const getGalleryImageIdsFromUrls = (urls = []) => {
    const matchedIds = urls
      .map((url) => galleryImageByUrl.get(url))
      .filter(Boolean);
    return Array.from(new Set(matchedIds));
  };

  const getGalleryImageUrls = (ids = []) => {
    return ids
      .map((id) => galleryImageMap.get(id)?.image)
      .filter(Boolean);
  };

  const getPackagePreviewImage = (pkg) => {
    const galleryIds = Array.isArray(pkg.galleryImageIds) ? pkg.galleryImageIds : [];
    const galleryImageUrl = galleryIds.length > 0 ? galleryImageMap.get(galleryIds[0])?.image : null;
    return galleryImageUrl || pkg.images?.[0] || pkg.image || null;
  };

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
  };

  const openCreateModal = () => {
    setModalMode('create');
    setFormData({
      name: '',
      description: '',
      categoryId: '',
      subcategoryName: '',
      price: '',
      duration: '',
      includes: '',
      image: '',
      images: [],
      galleryImageIds: [],
      availableAddOns: [],
      isActive: true,
    });
    setImageSourceType('file');
    setPackageImageFiles([]);
    setPackageImagePreviews([]);
    setImageUrlsInput('');
    setFileError('');
    setError(null);
    setShowModal(true);
  };

  const openEditModal = (item) => {
    setModalMode('edit');
    setSelectedItem(item);

    if (activeTab === 'packages') {
      const normalizedImages = Array.isArray(item.images)
        ? item.images.filter(Boolean)
        : item.image
        ? [item.image]
        : [];

      const existingGalleryImageIds = Array.isArray(item.galleryImageIds)
        ? item.galleryImageIds.filter(Boolean)
        : [];
      const matchedGalleryImageIds =
        existingGalleryImageIds.length > 0
          ? existingGalleryImageIds
          : getGalleryImageIdsFromUrls(normalizedImages);

      setFormData({
        name: item.name || '',
        description: item.description || '',
        categoryId: item.categoryId || '',
        subcategoryName: item.subcategoryName || '',
        price: item.price || '',
        duration: item.duration || '',
        includes: (item.includes || []).join('\n'),
        image: item.image || '',
        images: normalizedImages,
        galleryImageIds: matchedGalleryImageIds,
        availableAddOns: item.availableAddOns || [],
        isActive: item.isActive !== undefined ? item.isActive : true,
      });
      setImageSourceType(existingGalleryImageIds.length > 0 ? 'gallery' : 'url');
      setPackageImageFiles([]);
      setPackageImagePreviews([]);
      setImageUrlsInput(normalizedImages.join('\n'));
      setFileError('');
    } else {
      setFormData({
        name: item.name || '',
        description: item.description || '',
        price: item.price || '',
        isActive: item.isActive !== undefined ? item.isActive : true,
      });
    }

    setError(null);
    setShowModal(true);
  };

  const openDeleteModal = async (item) => {
    const confirmed = await confirm(
      `Hapus ${activeTab === 'packages' ? 'paket' : 'add-on'} "${item.name}"? Tindakan tidak dapat dibatalkan.`,
      {
        title: `Hapus ${activeTab === 'packages' ? 'Paket' : 'Add-on'}`,
        type: 'danger',
        confirmText: 'Ya, Hapus',
        cancelText: 'Batal',
      }
    );

    if (!confirmed) return;

    setIsSubmitting(true);
    setError(null);

    try {
      await toastPromise(
        activeTab === 'packages'
          ? deletePackage(item.id)
          : deleteAddOn(item.id),
        {
          loading: 'Menghapus...',
          success: activeTab === 'packages' ? 'Paket berhasil dihapus!' : 'Add-on berhasil dihapus!',
          error: 'Gagal menghapus',
        }
      );
    } catch (error) {
      console.error('Error deleting item:', error);
      setError(error.message || 'Gagal menghapus');
      toastError(error.message || 'Gagal menghapus');
    } finally {
      setIsSubmitting(false);
    }
  };

  const closeModals = () => {
    setShowModal(false);
    setShowDeleteModal(false);
    setSelectedItem(null);
    setItemToDelete(null);
    setError(null);
    setSuccess(null);
    setImageSourceType('file');
    setPackageImageFiles([]);
    setPackageImagePreviews([]);
    setImageUrlsInput('');
    setFileError('');
    setUploadingImage(false);
  };

  const handleMigration = async () => {
    setIsMigrating(true);
    setMigrationResult(null);
    setError(null);

    try {
      const result = await migrateFromMock({ services: mockServices });
      setMigrationResult(result);
      setSuccess('Migrasi data berhasil!');

      setTimeout(() => {
        setMigrationResult(null);
        setSuccess(null);
      }, 5000);
    } catch (error) {
      console.error('Migration error:', error);
      setError('Gagal melakukan migrasi: ' + error.message);
    } finally {
      setIsMigrating(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value,
    });
  };

  const handleAvailableAddOnChange = (addOnId) => {
    const updated = formData.availableAddOns.includes(addOnId)
      ? formData.availableAddOns.filter((id) => id !== addOnId)
      : [...formData.availableAddOns, addOnId];

    setFormData({ ...formData, availableAddOns: updated });
  };

  useEffect(() => {
    if (imageSourceType === 'url') {
      setImageUrlsInput((formData.images || []).join('\n'));
    }
  }, [imageSourceType, formData.images]);

  const handlePackageImageFileChange = (e) => {
    const files = Array.from(e.target.files || []);

    if (files.length === 0) {
      setPackageImageFiles([]);
      setFileError('');
      return;
    }

    const invalidFile = files.find(
      (file) => !file.type.startsWith('image/') || file.size > 2 * 1024 * 1024
    );

    if (invalidFile) {
      setPackageImageFiles([]);
      setFileError('Pastikan semua file berupa gambar dan maksimal 2 MB');
      return;
    }

    setPackageImageFiles((prev) => [...prev, ...files]);
    setFileError('');
  };

  const handleRemovePackageImageFile = (index) => {
    setPackageImageFiles((prev) => prev.filter((_, idx) => idx !== index));
  };

  const handleImageUrlsChange = (value) => {
    const urls = value
      .split('\n')
      .map((line) => line.trim())
      .filter(Boolean);

    setImageUrlsInput(value);
    setFormData((prev) => ({ ...prev, images: urls }));
  };

  const handleToggleGalleryImage = (imageId) => {
    setFormData((prev) => {
      if (!imageId) return prev;
      const galleryImageIds = Array.isArray(prev.galleryImageIds) ? prev.galleryImageIds : [];
      const nextGalleryImageIds = galleryImageIds.includes(imageId)
        ? galleryImageIds.filter((id) => id !== imageId)
        : [...galleryImageIds, imageId];

      return { ...prev, galleryImageIds: nextGalleryImageIds };
    });
  };

  const handleRemoveGalleryImage = (imageId) => {
    setFormData((prev) => {
      const galleryImageIds = (prev.galleryImageIds || []).filter((id) => id !== imageId);
      return { ...prev, galleryImageIds };
    });
  };

  const handleRemoveImageUrl = (imageUrl) => {
    setFormData((prev) => {
      const images = (prev.images || []).filter((url) => url !== imageUrl);
      if (imageSourceType === 'url') {
        setImageUrlsInput(images.join('\n'));
      }
      return { ...prev, images };
    });
  };

  const validateForm = () => {
    if (!formData.name.trim()) {
      setError('Nama harus diisi');
      return false;
    }
    if (activeTab === 'packages' && !formData.categoryId) {
      setError('Kategori harus dipilih');
      return false;
    }
    if (!formData.price || parseFloat(formData.price) < 0) {
      setError('Harga harus diisi dengan nilai yang valid');
      return false;
    }

    if (activeTab === 'packages' && imageSourceType === 'url' && imageUrlsInput.trim()) {
      const urls = imageUrlsInput
        .split('\n')
        .map((line) => line.trim())
        .filter(Boolean);
      const invalidUrl = urls.find((url) => {
        try {
          new URL(url);
          return false;
        } catch {
          return true;
        }
      });
      if (invalidUrl) {
        setError('Terdapat link gambar yang tidak valid');
        return false;
      }
    }

    if (activeTab === 'packages' && fileError) {
      setError(fileError);
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    if (!validateForm()) return;

    setIsSubmitting(true);

    try {
      if (activeTab === 'packages') {
        let existingImages = Array.isArray(formData.images) ? [...formData.images] : [];
        const selectedGalleryImageIds = Array.isArray(formData.galleryImageIds)
          ? formData.galleryImageIds.filter(Boolean)
          : [];

        if (imageSourceType === 'url') {
          existingImages = imageUrlsInput
            .split('\n')
            .map((line) => line.trim())
            .filter(Boolean);
        }

        let uploadedImages = [];
        if (imageSourceType === 'file' && packageImageFiles.length > 0) {
          setUploadingImage(true);
          const uploadResults = await Promise.all(
            packageImageFiles.map((file) => uploadGalleryImage(file))
          );
          uploadedImages = uploadResults
            .map((result) => result?.dataUrl)
            .filter(Boolean);
        }

        const normalizedImages = [...existingImages, ...uploadedImages].filter(Boolean);
        const packageImage = normalizedImages[0] || null;

        const packageData = {
          name: formData.name,
          description: formData.description,
          categoryId: formData.categoryId,
          subcategoryName: formData.subcategoryName || null,
          price: parseFloat(formData.price),
          duration: formData.duration || null,
          includes: formData.includes.split('\n').filter((i) => i.trim()),
          image: imageSourceType === 'gallery' ? null : packageImage,
          images: imageSourceType === 'gallery' ? [] : normalizedImages,
          galleryImageIds: selectedGalleryImageIds,
          availableAddOns: formData.availableAddOns,
          isActive: formData.isActive,
        };

        await toastPromise(
          modalMode === 'create'
            ? createPackage(packageData)
            : updatePackage(selectedItem.id, packageData),
          {
            loading: modalMode === 'create' ? 'Menambahkan paket...' : 'Memperbarui paket...',
            success: modalMode === 'create' ? 'Paket berhasil ditambahkan!' : 'Paket berhasil diperbarui!',
            error: 'Gagal menyimpan paket',
          }
        );
      } else {
        const addOnData = {
          name: formData.name,
          description: formData.description,
          price: parseFloat(formData.price),
          isActive: formData.isActive,
        };

        await toastPromise(
          modalMode === 'create'
            ? createAddOn(addOnData)
            : updateAddOn(selectedItem.id, addOnData),
          {
            loading: modalMode === 'create' ? 'Menambahkan add-on...' : 'Memperbarui add-on...',
            success: modalMode === 'create' ? 'Add-on berhasil ditambahkan!' : 'Add-on berhasil diperbarui!',
            error: 'Gagal menyimpan add-on',
          }
        );
      }

      closeModals();
    } catch (error) {
      console.error('Error saving item:', error);
      setError(error.message || 'Gagal menyimpan');
      toastError(error.message || 'Gagal menyimpan');
    } finally {
      setUploadingImage(false);
      setIsSubmitting(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Confirmation Dialog */}
      <ConfirmDialog />

      {/* Loading State */}
      {loading && (
        <div className="space-y-6">
          {/* Header Skeleton */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="space-y-2">
              <div className="h-8 w-48 bg-white/5 rounded animate-pulse" />
              <div className="h-4 w-64 bg-white/5 rounded animate-pulse" />
            </div>
            <div className="h-10 w-40 bg-white/5 rounded-lg animate-pulse" />
          </div>

          {/* Stats Skeleton */}
          <StatsCardSkeleton count={2} />

          {/* Tabs Skeleton */}
          <div className="h-14 bg-white/5 border border-[#D4AF7A]/20 rounded-lg animate-pulse" />

          {/* Search Skeleton */}
          <div className="h-12 bg-white/5 border border-[#D4AF7A]/20 rounded-lg animate-pulse" />
        </div>
      )}

      {/* Error State */}
      {!loading && error && (
        <ErrorState
          title="Gagal Memuat Data"
          message={error}
          onRetry={() => window.location.reload()}
        />
      )}

      {/* Content */}
      {!loading && !error && (
        <>
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-[#F7F5F2]">Paket & Layanan</h2>
          <p className="text-[#F7F5F2]/70">Kelola paket henna dan add-ons</p>
        </div>
        <button
          onClick={openCreateModal}
          className="flex items-center justify-center gap-2 px-4 py-2.5 bg-gradient-to-r from-[#C9A882] to-[#D4AF7A] text-black font-semibold rounded-lg hover:opacity-90 transition-opacity"
        >
          <Plus size={20} />
          {activeTab === 'packages' ? 'Tambah Paket' : 'Tambah Add-on'}
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="bg-white/5 border border-[#D4AF7A]/20 rounded-xl p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-[#F7F5F2]/70">Total Paket</p>
              <p className="text-2xl font-bold text-[#F7F5F2]">{packages.length}</p>
            </div>
            <Package className="w-8 h-8 text-[#D4AF7A]" />
          </div>
        </div>

        <div className="bg-white/5 border border-[#D4AF7A]/20 rounded-xl p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-[#F7F5F2]/70">Total Add-ons</p>
              <p className="text-2xl font-bold text-[#F7F5F2]">{addOns.length}</p>
            </div>
            <Layers className="w-8 h-8 text-[#C9A882]" />
          </div>
        </div>
      </div>

      {/* Migration Banner */}
      {packages.length === 0 && !loading && (
        <div className="bg-gradient-to-r from-blue-500/10 to-purple-500/10 border border-blue-500/30 rounded-xl p-6">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 bg-blue-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
              <Database className="w-6 h-6 text-blue-400" />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-blue-400 mb-2">
                Inisialisasi Data
              </h3>
              <p className="text-[#F7F5F2]/80 mb-4">
                Belum ada data paket. Import data dari mock data untuk memulai?
              </p>

              {/* Migration Result */}
              {migrationResult && (
                <div className="mb-4 p-4 bg-white/5 rounded-lg">
                  <div className="flex items-center gap-2 mb-2">
                    <CheckCircle className="w-5 h-5 text-green-400" />
                    <span className="font-semibold text-green-400">Migrasi Berhasil!</span>
                  </div>
                  <div className="text-sm text-[#F7F5F2]/80 space-y-1">
                    <p>✓ {migrationResult.categories.created} kategori dibuat</p>
                    <p>✓ {migrationResult.packages.created} paket dibuat</p>
                    {migrationResult.categories.errors.length > 0 && (
                      <p className="text-yellow-400">
                        ⚠ {migrationResult.categories.errors.length} kategori gagal
                      </p>
                    )}
                    {migrationResult.packages.errors.length > 0 && (
                      <p className="text-yellow-400">
                        ⚠ {migrationResult.packages.errors.length} paket gagal
                      </p>
                    )}
                  </div>
                </div>
              )}

              <div className="flex gap-3">
                <button
                  onClick={handleMigration}
                  disabled={isMigrating}
                  className="px-4 py-2 bg-blue-500 text-white font-semibold rounded-lg hover:bg-blue-600 transition-colors disabled:opacity-50 flex items-center gap-2"
                >
                  {isMigrating ? (
                    <>
                      <Loader2 size={16} className="animate-spin" />
                      Migrating...
                    </>
                  ) : (
                    <>
                      <Database size={16} />
                      Import Data dari Mock
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="border-b border-[#D4AF7A]/20">
        <nav className="flex gap-2">
          <button
            onClick={() => setActiveTab('packages')}
            className={`flex items-center gap-2 px-4 py-3 border-b-2 transition-all font-medium ${
              activeTab === 'packages'
                ? 'border-[#D4AF7A] text-[#D4AF7A]'
                : 'border-transparent text-[#F7F5F2]/60 hover:text-[#F7F5F2] hover:border-[#D4AF7A]/30'
            }`}
          >
            <Package size={18} />
            Paket Henna
          </button>
          <button
            onClick={() => setActiveTab('addons')}
            className={`flex items-center gap-2 px-4 py-3 border-b-2 transition-all font-medium ${
              activeTab === 'addons'
                ? 'border-[#D4AF7A] text-[#D4AF7A]'
                : 'border-transparent text-[#F7F5F2]/60 hover:text-[#F7F5F2] hover:border-[#D4AF7A]/30'
            }`}
          >
            <Layers size={18} />
            Add-ons
          </button>
        </nav>
      </div>

      {/* Search & Filter */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#F7F5F2]/50" />
          <input
            type="text"
            value={searchQuery}
            onChange={handleSearchChange}
            placeholder={`Cari ${activeTab === 'packages' ? 'paket' : 'add-on'}...`}
            className="w-full pl-10 pr-4 py-2 bg-white/5 border border-[#D4AF7A]/30 rounded-lg text-[#F7F5F2] placeholder-[#F7F5F2]/50 focus:outline-none focus:border-[#D4AF7A]"
          />
        </div>

        {activeTab === 'packages' && (
          <select
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
            className="px-4 py-2 bg-white/5 border border-[#D4AF7A]/30 rounded-lg text-[#F7F5F2] focus:outline-none focus:border-[#D4AF7A]"
          >
            <option value="all">Semua Kategori</option>
            {categories.map((cat) => (
              <option key={cat.id} value={cat.id}>
                {cat.name}
              </option>
            ))}
          </select>
        )}
      </div>

      {/* Content */}
      {activeTab === 'packages' ? (
        /* Packages Grid */
        filteredPackages.length === 0 ? (
          searchQuery || categoryFilter !== 'all' ? (
            <SearchEmptyState
              onClear={() => {
                setSearchQuery('');
                setCategoryFilter('all');
              }}
            />
          ) : (
            <PackagesEmptyState
              onAdd={() => openCreateModal()}
            />
          )
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredPackages.map((pkg) => (
              <div
                key={pkg.id}
                className={`bg-white/5 border rounded-xl p-6 transition-all ${
                  pkg.isActive === false
                    ? 'border-red-500/30 opacity-70'
                    : 'border-[#D4AF7A]/20'
                }`}
              >
                <div className="flex items-start justify-between mb-3">
                  <h4 className="font-semibold text-[#F7F5F2]">{pkg.name}</h4>
                  {pkg.isActive === false && (
                    <span className="px-2 py-1 text-xs font-medium bg-red-500/20 text-red-400 rounded-full">
                      Non-aktif
                    </span>
                  )}
                </div>
                <p className="text-xs text-[#F7F5F2]/60 mb-2">
                  {getCategoryName(pkg.categoryId)}
                  {pkg.subcategoryName && ` › ${pkg.subcategoryName}`}
                </p>
                <p className="text-sm text-[#F7F5F2]/70 mb-3 line-clamp-2">
                  {pkg.description || '-'}
                </p>
                 {getPackagePreviewImage(pkg) && (
                  <div className="mb-3 rounded-lg overflow-hidden border border-[#D4AF7A]/20">
                    <img
                        src={getPackagePreviewImage(pkg)}
                      alt={pkg.name}
                      className="w-full h-32 object-cover"
                      loading="lazy"
                    />
                  </div>
                 )}
                <p className="text-lg font-bold text-[#D4AF7A] mb-2">
                  Rp {pkg.price?.toLocaleString() || '0'}
                </p>
                {(pkg.availableAddOns || []).length > 0 && (
                  <p className="text-xs text-[#F7F5F2]/60 mb-4">
                    {pkg.availableAddOns.length} add-on tersedia
                  </p>
                )}
                <div className="flex gap-2">
                  <button
                    onClick={() => openEditModal(pkg)}
                    className="flex-1 flex items-center justify-center gap-1 px-3 py-1.5 bg-blue-500/10 text-blue-400 rounded-lg hover:bg-blue-500/20 transition-colors text-sm"
                  >
                    <Edit2 size={14} />
                    Edit
                  </button>
                  <button
                    onClick={() => openDeleteModal(pkg)}
                    className="flex-1 flex items-center justify-center gap-1 px-3 py-1.5 bg-red-500/10 text-red-400 rounded-lg hover:bg-red-500/20 transition-colors text-sm"
                  >
                    <Trash2 size={14} />
                    Hapus
                  </button>
                </div>
              </div>
            ))}
          </div>
        )
      ) : (
        /* Add-ons Grid */
        filteredAddOns.length === 0 ? (
          searchQuery ? (
            <SearchEmptyState
              onClear={() => {
                setSearchQuery('');
              }}
            />
          ) : (
            <PackagesEmptyState
              onAdd={() => openCreateModal()}
            />
          )
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredAddOns.map((addon) => (
              <div
                key={addon.id}
                className={`bg-white/5 border rounded-xl p-6 transition-all ${
                  addon.isActive === false
                    ? 'border-red-500/30 opacity-70'
                    : 'border-[#D4AF7A]/20'
                }`}
              >
                <div className="flex items-start justify-between mb-3">
                  <h4 className="font-semibold text-[#F7F5F2]">{addon.name}</h4>
                  {addon.isActive === false && (
                    <span className="px-2 py-1 text-xs font-medium bg-red-500/20 text-red-400 rounded-full">
                      Non-aktif
                    </span>
                  )}
                </div>
                <p className="text-sm text-[#F7F5F2]/70 mb-3 line-clamp-2">
                  {addon.description || '-'}
                </p>
                <p className="text-lg font-bold text-[#D4AF7A] mb-2">
                  Rp {addon.price?.toLocaleString() || '0'}
                </p>
                {(packages.filter(p => (p.availableAddOns || []).includes(addon.id))).length > 0 && (
                  <div className="flex flex-wrap gap-1 mb-4">
                    {packages
                      .filter(p => (p.availableAddOns || []).includes(addon.id))
                      .slice(0, 3)
                      .map(p => (
                        <span
                          key={p.id}
                          className="px-2 py-0.5 text-xs bg-[#D4AF7A]/20 text-[#D4AF7A] rounded-full"
                        >
                          {getCategoryName(p.categoryId)} - {p.name}
                        </span>
                      ))}
                    {packages.filter(p => (p.availableAddOns || []).includes(addon.id)).length > 3 && (
                      <span className="px-2 py-0.5 text-xs bg-white/10 text-[#F7F5F2]/70 rounded-full">
                        +{packages.filter(p => (p.availableAddOns || []).includes(addon.id)).length - 3} lagi
                      </span>
                    )}
                  </div>
                )}
                <div className="flex gap-2">
                  <button
                    onClick={() => openEditModal(addon)}
                    className="flex-1 flex items-center justify-center gap-1 px-3 py-1.5 bg-blue-500/10 text-blue-400 rounded-lg hover:bg-blue-500/20 transition-colors text-sm"
                  >
                    <Edit2 size={14} />
                    Edit
                  </button>
                  <button
                    onClick={() => openDeleteModal(addon)}
                    className="flex-1 flex items-center justify-center gap-1 px-3 py-1.5 bg-red-500/10 text-red-400 rounded-lg hover:bg-red-500/20 transition-colors text-sm"
                  >
                    <Trash2 size={14} />
                    Hapus
                  </button>
                </div>
              </div>
            ))}
          </div>
        )
      )}

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm overflow-y-auto">
          <div className="bg-[#0A0A0A] border border-[#D4AF7A]/20 rounded-xl w-full max-w-lg my-8">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-4 border-b border-[#D4AF7A]/20">
              <h3 className="text-lg font-semibold text-[#F7F5F2]">
                {modalMode === 'create'
                  ? activeTab === 'packages'
                    ? 'Tambah Paket Baru'
                    : 'Tambah Add-on Baru'
                  : activeTab === 'packages'
                  ? 'Edit Paket'
                  : 'Edit Add-on'}
              </h3>
              <button
                onClick={closeModals}
                className="p-1 text-[#F7F5F2]/70 hover:text-[#F7F5F2] transition-colors"
              >
                <X size={20} />
              </button>
            </div>

            {/* Modal Body */}
            <form onSubmit={handleSubmit} className="p-4 space-y-4">
              {/* Error Message */}
              {error && (
                <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
                  <p className="text-sm text-red-400">{error}</p>
                </div>
              )}

              {/* Success Message */}
              {success && (
                <div className="p-3 bg-green-500/10 border border-green-500/30 rounded-lg">
                  <p className="text-sm text-green-400">{success}</p>
                </div>
              )}

              {/* Name Input */}
              <div>
                <label className="block text-sm font-medium text-[#F7F5F2] mb-1.5">
                  Nama <span className="text-red-400">*</span>
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder="Contoh: Paket Wedding, Tambahan dekorasi, dll"
                  className="w-full px-3 py-2 bg-white/5 border border-[#D4AF7A]/30 rounded-lg text-[#F7F5F2] placeholder-[#F7F5F2]/50 focus:outline-none focus:border-[#D4AF7A]"
                  required
                />
              </div>

              {/* Description Input */}
              <div>
                <label className="block text-sm font-medium text-[#F7F5F2] mb-1.5">
                  Deskripsi
                </label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  placeholder="Deskripsi detail..."
                  rows={2}
                  className="w-full px-3 py-2 bg-white/5 border border-[#D4AF7A]/30 rounded-lg text-[#F7F5F2] placeholder-[#F7F5F2]/50 focus:outline-none focus:border-[#D4AF7A] resize-none"
                />
              </div>

              {activeTab === 'packages' ? (
                <>
                  {/* Category Select */}
                  <div>
                    <label className="block text-sm font-medium text-[#F7F5F2] mb-1.5">
                      Kategori <span className="text-red-400">*</span>
                    </label>
                    <select
                      name="categoryId"
                      value={formData.categoryId}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 bg-white/5 border border-[#D4AF7A]/30 rounded-lg text-[#F7F5F2] focus:outline-none focus:border-[#D4AF7A]"
                      required
                    >
                      <option value="">Pilih kategori</option>
                      {categories.map((cat) => (
                        <option key={cat.id} value={cat.id}>
                          {cat.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Subcategory Input */}
                  <div>
                    <label className="block text-sm font-medium text-[#F7F5F2] mb-1.5">
                      Subkategori
                    </label>
                    <input
                      type="text"
                      name="subcategoryName"
                      value={formData.subcategoryName}
                      onChange={handleInputChange}
                      placeholder="Contoh: Simple, Full Body, dll"
                      className="w-full px-3 py-2 bg-white/5 border border-[#D4AF7A]/30 rounded-lg text-[#F7F5F2] placeholder-[#F7F5F2]/50 focus:outline-none focus:border-[#D4AF7A]"
                    />
                  </div>

                  {/* Price Input */}
                  <div>
                    <label className="block text-sm font-medium text-[#F7F5F2] mb-1.5">
                      Harga (Rp) <span className="text-red-400">*</span>
                    </label>
                    <input
                      type="number"
                      name="price"
                      value={formData.price}
                      onChange={handleInputChange}
                      placeholder="0"
                      min="0"
                      step="1000"
                      className="w-full px-3 py-2 bg-white/5 border border-[#D4AF7A]/30 rounded-lg text-[#F7F5F2] placeholder-[#F7F5F2]/50 focus:outline-none focus:border-[#D4AF7A]"
                      required
                    />
                  </div>

                  {/* Duration Input */}
                  <div>
                    <label className="block text-sm font-medium text-[#F7F5F2] mb-1.5">
                      Durasi
                    </label>
                    <input
                      type="text"
                      name="duration"
                      value={formData.duration}
                      onChange={handleInputChange}
                      placeholder="Contoh: 2 jam, 3-4 jam, dll"
                      className="w-full px-3 py-2 bg-white/5 border border-[#D4AF7A]/30 rounded-lg text-[#F7F5F2] placeholder-[#F7F5F2]/50 focus:outline-none focus:border-[#D4AF7A]"
                    />
                  </div>

                  {/* Includes Input */}
                  <div>
                    <label className="block text-sm font-medium text-[#F7F5F2] mb-1.5">
                      Termasuk (satu per baris)
                    </label>
                    <textarea
                      name="includes"
                      value={formData.includes}
                      onChange={handleInputChange}
                      placeholder="• Henna untuk tangan & kaki&#10;• Desain custom&#10;• dll"
                      rows={4}
                      className="w-full px-3 py-2 bg-white/5 border border-[#D4AF7A]/30 rounded-lg text-[#F7F5F2] placeholder-[#F7F5F2]/50 focus:outline-none focus:border-[#D4AF7A] resize-none"
                    />
                  </div>

                  {/* Available Add-ons */}
                  <div>
                    <label className="block text-sm font-medium text-[#F7F5F2] mb-1.5">
                      Add-ons Tersedia
                    </label>
                    <div className="space-y-2 max-h-40 overflow-y-auto bg-white/5 border border-[#D4AF7A]/30 rounded-lg p-3">
                      {addOns.length === 0 ? (
                        <p className="text-xs text-[#F7F5F2]/60">Belum ada add-on. Buat add-on terlebih dahulu di tab Add-ons.</p>
                      ) : (
                        addOns.map((addon) => (
                          <label
                            key={addon.id}
                            className="flex items-center gap-2 cursor-pointer"
                          >
                            <input
                              type="checkbox"
                              checked={formData.availableAddOns.includes(addon.id)}
                              onChange={() => handleAvailableAddOnChange(addon.id)}
                              className="w-4 h-4 rounded border-[#D4AF7A]/30 text-[#D4AF7A] focus:ring-[#D4AF7A] focus:ring-offset-0 bg-white/5"
                            />
                            <span className="text-sm text-[#F7F5F2]">{addon.name}</span>
                            <span className="text-xs text-[#F7F5F2]/50 ml-auto">
                              Rp {addon.price?.toLocaleString()}
                            </span>
                          </label>
                        ))
                      )}
                    </div>
                  </div>

                   {/* Package Sample Image */}
                   <div className="space-y-3">
                     <label className="block text-sm font-medium text-[#F7F5F2]">
                       Gambar Sampel Paket (Opsional)
                     </label>

                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={() => setImageSourceType('file')}
                        className={`flex-1 px-3 py-2 rounded-lg text-sm transition-colors ${
                          imageSourceType === 'file'
                            ? 'bg-[#D4AF7A] text-black'
                            : 'bg-white/5 text-[#F7F5F2]'
                        }`}
                      >
                        <Upload size={14} className="inline mr-1.5" />
                        Upload
                      </button>
                      <button
                        type="button"
                        onClick={() => setImageSourceType('url')}
                        className={`flex-1 px-3 py-2 rounded-lg text-sm transition-colors ${
                          imageSourceType === 'url'
                            ? 'bg-[#D4AF7A] text-black'
                            : 'bg-white/5 text-[#F7F5F2]'
                        }`}
                      >
                        <LinkIcon size={14} className="inline mr-1.5" />
                        Link
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setImageSourceType('gallery');
                          setPackageImageFiles([]);
                          setFileError('');
                        }}
                        className={`flex-1 px-3 py-2 rounded-lg text-sm transition-colors ${
                          imageSourceType === 'gallery'
                            ? 'bg-[#D4AF7A] text-black'
                            : 'bg-white/5 text-[#F7F5F2]'
                        }`}
                      >
                        <ImageIcon size={14} className="inline mr-1.5" />
                        Gallery
                      </button>
                    </div>

                    {imageSourceType === 'file' ? (
                      <div>
                        <input
                          type="file"
                          accept="image/*"
                          multiple
                          onChange={handlePackageImageFileChange}
                          className="w-full px-3 py-2 bg-white/5 border border-[#D4AF7A]/30 rounded-lg text-[#F7F5F2] file:mr-3 file:py-1.5 file:px-3 file:rounded-md file:border-0 file:bg-[#D4AF7A] file:text-black file:cursor-pointer"
                        />
                        <p className="text-xs text-[#F7F5F2]/60 mt-1">Maksimal 2 MB per gambar</p>
                        {fileError && <p className="text-xs text-red-400 mt-1">{fileError}</p>}
                        {packageImagePreviews.length > 0 && (
                          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mt-3">
                            {packageImagePreviews.map((preview, index) => (
                              <div
                                key={`${preview.name}-${index}`}
                                className="relative rounded-md overflow-hidden border border-[#D4AF7A]/20"
                              >
                                <img
                                  src={preview.url}
                                  alt={`Preview ${index + 1}`}
                                  className="w-full h-24 object-cover"
                                />
                                <button
                                  type="button"
                                  onClick={() => handleRemovePackageImageFile(index)}
                                  className="absolute top-1 right-1 bg-black/60 text-white rounded-full p-1 hover:bg-black/80"
                                  aria-label="Hapus gambar"
                                >
                                  <X size={12} />
                                </button>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    ) : imageSourceType === 'url' ? (
                      <div className="space-y-2">
                        <textarea
                          value={imageUrlsInput}
                          onChange={(e) => handleImageUrlsChange(e.target.value)}
                          placeholder="https://example.com/sample-1.jpg\nhttps://example.com/sample-2.jpg"
                          rows={3}
                          className="w-full px-3 py-2 bg-white/5 border border-[#D4AF7A]/30 rounded-lg text-[#F7F5F2] placeholder-[#F7F5F2]/50 focus:outline-none focus:border-[#D4AF7A] resize-none"
                        />
                        <p className="text-xs text-[#F7F5F2]/60">Satu URL per baris</p>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        <div className="max-h-56 overflow-y-auto grid grid-cols-2 sm:grid-cols-3 gap-2 rounded-lg border border-[#D4AF7A]/20 bg-white/5 p-2">
                          {galleryItems.length === 0 ? (
                            <p className="col-span-full text-xs text-[#F7F5F2]/60 text-center py-4">
                              Belum ada gambar di gallery.
                            </p>
                          ) : (
                             galleryItems.map((item) => {
                               const isSelected = (formData.galleryImageIds || []).includes(item.id);

                              return (
                                <button
                                  key={item.id}
                                  type="button"
                                  onClick={() => {
                                     handleToggleGalleryImage(item.id || '');
                                     setPackageImageFiles([]);
                                     setFileError('');
                                   }}
                                  className={`relative overflow-hidden rounded-md border-2 transition-colors ${
                                    isSelected
                                      ? 'border-[#D4AF7A]'
                                      : 'border-transparent hover:border-[#D4AF7A]/50'
                                  }`}
                                  title={item.title || 'Gallery image'}
                                >
                                  <img
                                    src={item.image}
                                    alt={item.title || 'Gallery image'}
                                    className="w-full h-20 object-cover"
                                    loading="lazy"
                                  />
                                </button>
                              );
                            })
                          )}
                        </div>
                        <p className="text-xs text-[#F7F5F2]/60">
                          Pilih lebih dari satu gambar dari Gallery
                        </p>
                      </div>
                    )}

                    {(formData.galleryImageIds || []).length > 0 && (
                      <div className="rounded-lg border border-[#D4AF7A]/20 bg-white/5 p-3">
                        <p className="text-xs text-[#F7F5F2]/70 mb-2">Gallery terpilih</p>
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                          {formData.galleryImageIds.map((imageId, index) => {
                            const imageUrl = galleryImageMap.get(imageId)?.image;
                            if (!imageUrl) return null;
                            return (
                            <div
                              key={`${imageId}-${index}`}
                              className="relative rounded-md overflow-hidden border border-[#D4AF7A]/20"
                            >
                              <img
                                src={imageUrl}
                                alt={`Gambar gallery ${index + 1}`}
                                className="w-full h-20 object-cover"
                              />
                              <button
                                type="button"
                                onClick={() => handleRemoveGalleryImage(imageId)}
                                className="absolute top-1 right-1 bg-black/60 text-white rounded-full p-1 hover:bg-black/80"
                                aria-label="Hapus gambar"
                              >
                                <X size={12} />
                              </button>
                            </div>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {(formData.images || []).length > 0 && (
                      <div className="rounded-lg border border-[#D4AF7A]/20 bg-white/5 p-3">
                        <p className="text-xs text-[#F7F5F2]/70 mb-2">Gambar terpilih</p>
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                          {formData.images.map((imageUrl, index) => (
                            <div
                              key={`${imageUrl}-${index}`}
                              className="relative rounded-md overflow-hidden border border-[#D4AF7A]/20"
                            >
                              <img
                                src={imageUrl}
                                alt={`Gambar paket ${index + 1}`}
                                className="w-full h-20 object-cover"
                              />
                              <button
                                type="button"
                                onClick={() => handleRemoveImageUrl(imageUrl)}
                                className="absolute top-1 right-1 bg-black/60 text-white rounded-full p-1 hover:bg-black/80"
                                aria-label="Hapus gambar"
                              >
                                <X size={12} />
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </>
              ) : (
                <>
                  {/* Price Input for Add-on */}
                  <div>
                    <label className="block text-sm font-medium text-[#F7F5F2] mb-1.5">
                      Harga (Rp) <span className="text-red-400">*</span>
                    </label>
                    <input
                      type="number"
                      name="price"
                      value={formData.price}
                      onChange={handleInputChange}
                      placeholder="0"
                      min="0"
                      step="1000"
                      className="w-full px-3 py-2 bg-white/5 border border-[#D4AF7A]/30 rounded-lg text-[#F7F5F2] placeholder-[#F7F5F2]/50 focus:outline-none focus:border-[#D4AF7A]"
                      required
                    />
                  </div>

                  {/* Package Assignment Info */}
                  <div>
                    <label className="block text-sm font-medium text-[#F7F5F2] mb-1.5">
                      Tersedia di Paket
                    </label>
                    <div className="p-3 bg-white/5 border border-[#D4AF7A]/30 rounded-lg">
                      {modalMode === 'create' ? (
                        <p className="text-xs text-[#F7F5F2]/60">
                          Setelah add-on dibuat, atur ketersediaannya di form edit paket masing-masing.
                        </p>
                      ) : (
                        <div className="flex flex-wrap gap-1">
                          {packages.filter(p => (p.availableAddOns || []).includes(selectedItem?.id)).length === 0 ? (
                            <p className="text-xs text-[#F7F5F2]/60">
                              Belum ditambahkan ke paket manapun. Atur di form edit paket.
                            </p>
                          ) : (
                            packages
                              .filter(p => (p.availableAddOns || []).includes(selectedItem?.id))
                              .map(p => (
                                <span
                                  key={p.id}
                                  className="px-2 py-0.5 text-xs bg-[#D4AF7A]/20 text-[#D4AF7A] rounded-full"
                                >
                                  {getCategoryName(p.categoryId)} - {p.name}
                                </span>
                              ))
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </>
              )}

              {/* Active Toggle */}
              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  name="isActive"
                  id="isActive"
                  checked={formData.isActive}
                  onChange={handleInputChange}
                  className="w-4 h-4 rounded border-[#D4AF7A]/30 text-[#D4AF7A] focus:ring-[#D4AF7A] focus:ring-offset-0 bg-white/5"
                />
                <label htmlFor="isActive" className="text-sm text-[#F7F5F2]">
                  Aktif
                </label>
              </div>

              {/* Modal Footer */}
              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={closeModals}
                  className="flex-1 px-4 py-2 bg-white/5 text-[#F7F5F2] rounded-lg hover:bg-white/10 transition-colors"
                  disabled={isSubmitting}
                >
                  Batal
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting || uploadingImage}
                  className="flex-1 px-4 py-2 bg-gradient-to-r from-[#C9A882] to-[#D4AF7A] text-black font-semibold rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50"
                >
                  {isSubmitting || uploadingImage ? (
                    <span className="flex items-center justify-center gap-2">
                      <Loader2 size={16} className="animate-spin" />
                      {uploadingImage ? 'Mengunggah gambar...' : 'Menyimpan...'}
                    </span>
                  ) : modalMode === 'create' ? (
                    'Tambah'
                  ) : (
                    'Simpan'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
        </>
      )}
    </div>
  );
};

export default AdminPackages;
